import { cn } from '@rocco/utils/cn';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { RICH_TEXT_CLASS } from '~/utils/rich-text';
import type Product from '../../type/product';

interface LibraryTab {
    ID: number;
    Title: string;
    Content?: string | null;
    MemberSortOrder: number;
}

interface InformationTabProps {
    data: {
        SpecificationTab: LibraryTab | undefined;
        PerformanceDescription: string;
        ScopeOfUseDescription: string;
        LibraryTabs: LibraryTab[];
    };
    professional: Pick<Product['Professional'], 'ID' | 'Title'>;
}

interface InformationPanel {
    key: string;
    title: string;
    content: string;
}

const InformationTab = ({ data, professional }: InformationTabProps) => {
    const {
        SpecificationTab,
        ScopeOfUseDescription,
        PerformanceDescription,
        LibraryTabs,
    } = data;
    const informationSections: InformationPanel[] = [];
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: professional.ID,
        professionalTitle: professional.Title,
        itemType: 'Product',
    });

    if (SpecificationTab) {
        informationSections.push({
            key: 'specifications',
            title: 'Specifications',
            content: SpecificationTab.Content || '',
        });
    }

    if (ScopeOfUseDescription) {
        informationSections.push({
            key: 'scope-of-use',
            title: 'Scope of use',
            content: ScopeOfUseDescription,
        });
    }
    if (PerformanceDescription) {
        informationSections.push({
            key: 'features',
            title: 'Features',
            content: PerformanceDescription,
        });
    }

    if (LibraryTabs.length) {
        LibraryTabs.sort(
            (a, b) => b.MemberSortOrder - a.MemberSortOrder
        ).forEach(({ Title, Content }, i) => {
            if (Content) {
                informationSections.push({
                    key: `custom-panel-${i}`,
                    title: Title,
                    content: Content,
                });
            }
        });
    }

    return (
        <div className="grid grid-cols-1 gap-5 md:gap-16">
            {informationSections.map((section) => (
                <div key={section.key}>
                    <div className="mb-3 text-lg font-medium leading-normal tracking-wide text-gray-800 md:mb-4">
                        {section.title}
                    </div>
                    <div
                        className={cn(
                            'border-b border-black pb-5 text-sm leading-[1.3] text-gray-800 md:text-base md:leading-normal',
                            RICH_TEXT_CLASS
                        )}
                        dangerouslySetInnerHTML={{
                            __html: section.content,
                        }}
                        onClick={clickProxy}
                        onContextMenu={clickProxy}
                    />
                </div>
            ))}
        </div>
    );
};

export default InformationTab;
